import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Check, ShieldCheck, Briefcase } from "lucide-react";
import { api } from "../lib/api";
import { StarRating, ProductCard } from "../components/cards";
import { ShareButtons } from "../components/ShareButtons";
import { CTASection } from "../components/CTASection";
import { GrowthCTA } from "../components/GrowthCTA";
import { mdToHtml } from "../lib/md";
import { useSeo, SITE_URL } from "../lib/seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/products/${slug}`).then(({ data }) => setData(data)).catch(() => {}); }, [slug]);

  const p = data?.product;
  const trackBuy = () => { if (p) api.post("/affiliate/click", { slug: p.slug, name: p.name, category: p.category }).catch(() => {}); };
  const pdesc = p ? (p.review_excerpt || `${p.name} review: ${p.rating}★ across ${p.review_count} reviews. Pain points, pros & cons, and whether it's worth buying.`) : "";
  useSeo(p ? {
    title: `${p.name} Review — ${p.rating}★ | San Antonio's Web Design`,
    description: pdesc,
    path: `/shop/${p.slug}`,
    image: p.image,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": p.name,
      "image": p.image,
      "description": pdesc,
      "brand": { "@type": "Brand", "name": p.brand || "San Antonio's Web Design" },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": p.rating,
        "reviewCount": p.review_count,
        "bestRating": 5,
      },
      "review": {
        "@type": "Review",
        "reviewRating": { "@type": "Rating", "ratingValue": p.rating, "bestRating": 5 },
        "author": { "@type": "Organization", "name": "San Antonio's Web Design" },
      },
      "offers": {
        "@type": "Offer",
        "price": p.price,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": SITE_URL + `/shop/${p.slug}`,
      },
    },
  } : {});

  if (!data) return <div className="mx-auto max-w-4xl px-6 py-24 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16" data-testid="product-detail">
      <div className="grid md:grid-cols-2 gap-10">
        <div className="rounded-3xl overflow-hidden glass">
          <img src={p.image} alt={p.name} className="w-full aspect-square object-cover" />
        </div>
        <div>
          <span className="text-sm font-semibold text-primary uppercase tracking-wide">{p.category?.replace("-", " ")}</span>
          <h1 className="font-head text-3xl md:text-4xl font-black mt-1">{p.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <StarRating value={p.rating} size={18} />
            <span className="text-muted-foreground">{p.rating} · {p.review_count?.toLocaleString?.() || p.review_count} reviews</span>
          </div>
          <div className="font-head text-4xl font-black text-primary mt-5">${p.price?.toLocaleString?.(undefined, { minimumFractionDigits: 2 })}</div>
          <p className="text-muted-foreground mt-4">{p.description}</p>

          {p.business_use && (
            <div className="mt-5 rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="product-business-use">
              <p className="font-semibold text-sm flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /> {p.business_use}</p>
              {p.business_blurb && <p className="text-sm text-muted-foreground mt-1">{p.business_blurb}</p>}
              {p.business_industries?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="text-xs text-muted-foreground mr-1">Ideal for:</span>
                  {p.business_industries.map((ind) => (
                    <Link key={ind} to={`/template-recommender?industry=${encodeURIComponent(ind)}`}
                      className="text-xs rounded-full bg-secondary px-2.5 py-1 hover:bg-primary/10 hover:text-primary transition-colors">{ind}</Link>
                  ))}
                </div>
              )}
            </div>
          )}

          <ul className="mt-5 space-y-2">
            {p.features?.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-primary" /> {f}</li>)}
          </ul>
          <a data-testid="buy-on-amazon" href={p.affiliate_url} onClick={trackBuy} target="_blank" rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 h-13 py-3.5 font-semibold hover:brightness-110 transition-[filter] shadow-lg shadow-primary/30">
            Buy on Amazon <ExternalLink className="h-4 w-4" />
          </a>
          <div className="mt-4"><ShareButtons title={p.name} /></div>
          <p className="mt-5 text-xs text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Verified 4.3★+ Amazon rating · As an Amazon Associate we earn from qualifying purchases.</p>
        </div>
      </div>

      {p.review_body && (
        <div className="mt-14 max-w-3xl" data-testid="product-review">
          <h2 className="font-head text-2xl md:text-3xl font-black mb-2">{p.name} Review</h2>
          <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Reviewed by our San Antonio tech team · {p.rating}★ · {p.review_count?.toLocaleString?.() || p.review_count} Amazon reviews
          </p>
          <div className="prose-sawd" dangerouslySetInnerHTML={{ __html: mdToHtml(p.review_body) }} />
          <a data-testid="review-buy-amazon" href={p.affiliate_url} onClick={trackBuy} target="_blank" rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 h-12 py-3 font-semibold hover:brightness-110 transition-[filter] shadow-lg shadow-primary/30">
            Check Price on Amazon <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}

      <GrowthCTA industry={data.product?.business_industries?.[0] || ""} label="Want the right tech AND a website that wins customers? Get your free Business Growth Score." />

      {data.related?.length > 0 && (
        <div className="mt-16">
          <h2 className="font-head text-2xl font-extrabold mb-6">You may also like</h2>
          <div className="grid gap-5 grid-cols-2 lg:grid-cols-4" data-testid="related-products">
            {data.related.map((r) => <ProductCard key={r.slug} p={r} />)}
          </div>
        </div>
      )}
      <CTASection title="Need this set up?" subtitle="Our IT team installs and configures your devices — book a free call." />
    </div>
  );
}
